'use client';

import { c as _c } from "react/compiler-runtime";
import ParallaxMedia from '@components/parallax-media/ParallaxMedia';
import Gradient_4_mob from '@images/gradients/gradient_4_mob.webp';
import Gradient_5 from '@images/gradients/gradient_5.webp';
import { MAIN_SLIDER_DATA_29719 } from '@lib/flags';
import { LINK_PREFIX, scrollInfo } from '@lib/tools/helpers/constants';
import { SafeHtmlRenderer } from '@lib/tools/helpers/SafeHtmlRenderer';
import { useViewport } from '@lib/tools/hooks/useViewport';
import cn from 'classnames';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import DOMPurify from 'isomorphic-dompurify';
import NextImage from 'next/image';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import style from './HorizontalScroll.module.scss';
function HorizontalScroll(t0) {
  const $ = _c(35);
  const {
    sliderData,
    isMobile,
    isTablet
  } = t0;
  const [st1, setSt1] = useState(null);
  const [st2, setSt2] = useState(null);
  const [st3, setSt3] = useState(null);
  const [st4, setSt4] = useState(null);
  const [classNames, setClassNames] = useState(style.horizontalScrollContainer);
  const horizontalRef = useRef(null);
  const scrollRef = useRef(null);
  const {
    viewport,
    isLandscape,
    userAgent
  } = useViewport();
  let t1;
  if ($[0] !== sliderData?.items) {
    t1 = sliderData?.items || [];
    $[0] = sliderData?.items;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const items = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [{
      rotate: 0.82,
      top: 5
    }, {
      rotate: -2.42,
      top: 0
    }, {
      rotate: 0.1,
      top: 5
    }, {
      rotate: -1.16,
      top: 0
    }, {
      rotate: 1.29,
      top: 5
    }, {
      rotate: 0.82,
      top: 0
    }, {
      rotate: -2.42,
      top: 5
    }, {
      rotate: 0.1,
      top: 0
    }, {
      rotate: -1.16,
      top: 5
    }, {
      rotate: 1.29,
      top: 0
    }];
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const slidesPositions = t2;
  const hasViewport = viewport;
  const isDesktopLandscape = !isMobile && !isTablet && isLandscape;
  const hasRefs = scrollRef && horizontalRef;
  const hasItems = MAIN_SLIDER_DATA_29719 ? items.length > 0 : scrollInfo.length > 0;
  let t3;
  if ($[3] !== hasItems || $[4] !== hasViewport || $[5] !== isDesktopLandscape || $[6] !== isMobile || $[7] !== isTablet || $[8] !== items) {
    t3 = () => {
      if (hasViewport && isDesktopLandscape && hasRefs && hasItems) {
        const element = scrollRef.current;
        const scrollingElement = horizontalRef.current;
        const pinWrapWidth = scrollingElement.clientWidth;
        const horizontalScrollLength = pinWrapWidth - window.innerWidth;
        setTimeout(() => {
          setSt1(ScrollTrigger.create({
            trigger: element,
            start: "center center",
            end: `+=${horizontalScrollLength}`,
            scroller: !isMobile ? "#smooth-wrapper" : "body",
            scrub: true,
            pin: true,
            anticipatePin: 3,
            invalidateOnRefresh: true
          }));
          setSt2(ScrollTrigger.create({
            animation: gsap.to(scrollingElement, {
              x: -horizontalScrollLength,
              ease: "power1.inOut"
            }),
            trigger: scrollingElement,
            start: "center center",
            end: `+=${horizontalScrollLength}`,
            scroller: !isMobile ? "#smooth-wrapper" : "body",
            scrub: 1.5,
            invalidateOnRefresh: true
          }));
          MAIN_SLIDER_DATA_29719 ? items?.forEach((_, index) => {
            const rotateN = slidesPositions[index].rotate > 0 ? 6 : -6;
            setSt3(ScrollTrigger.create({
              animation: gsap.to(horizontalRef.current?.children[index], {
                transform: `rotate(${slidesPositions[index].rotate + rotateN}deg)`,
                ease: "power1.inOut"
              }),
              trigger: scrollingElement,
              start: "center center",
              end: `+=${horizontalScrollLength}`,
              scroller: !isMobile ? "#smooth-wrapper" : "body",
              invalidateOnRefresh: true,
              scrub: true
            }));
            return null;
          }) : scrollInfo?.forEach((__0, index_0) => {
            const rotateN_0 = scrollInfo[index_0].rotate > 0 ? 6 : -6;
            setSt3(ScrollTrigger.create({
              animation: gsap.to(horizontalRef.current?.children[index_0], {
                transform: `rotate(${scrollInfo[index_0].rotate + rotateN_0}deg)`,
                ease: "power1.inOut"
              }),
              trigger: scrollingElement,
              start: "center center",
              end: `+=${horizontalScrollLength}`,
              scroller: !isMobile ? "#smooth-wrapper" : "body",
              invalidateOnRefresh: true,
              scrub: true
            }));
            return null;
          });
        }, 1200);
      } else {
        if (scrollRef && horizontalRef && (isMobile || isTablet)) {
          setClassNames(_temp);
          setTimeout(() => {
            const stylesArray = MAIN_SLIDER_DATA_29719 ? slidesPositions : scrollInfo;
            const itemsArray = MAIN_SLIDER_DATA_29719 ? items : scrollInfo;
            itemsArray?.forEach((__1, index_1) => {
              const currentRotate = stylesArray[index_1].rotate;
              const rotateN_1 = currentRotate > 0 ? 6 : -6;
              setSt4(ScrollTrigger.create({
                animation: gsap.to(horizontalRef.current?.children[index_1], {
                  transform: `rotate(${currentRotate + rotateN_1}deg)`,
                  ease: "slow"
                }),
                trigger: scrollRef.current,
                start: "bottom bottom",
                scroller: ".horizontal",
                horizontal: true,
                scrub: true
              }));
              return null;
            });
          }, 1000);
        }
      }
    };
    $[3] = hasItems;
    $[4] = hasViewport;
    $[5] = isDesktopLandscape;
    $[6] = isMobile;
    $[7] = isTablet;
    $[8] = items;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== isLandscape || $[11] !== isMobile || $[12] !== items || $[13] !== userAgent || $[14] !== viewport) {
    t4 = [viewport, scrollRef, horizontalRef, isLandscape, userAgent, isMobile, items];
    $[10] = isLandscape;
    $[11] = isMobile;
    $[12] = items;
    $[13] = userAgent;
    $[14] = viewport;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  useLayoutEffect(t3, t4);
  let t5;
  if ($[16] !== st1 || $[17] !== st2 || $[18] !== st3 || $[19] !== st4) {
    t5 = () => () => {
      st1?.kill();
      st2?.kill();
      st3?.kill();
      st4?.kill();
      setSt1(null);
      setSt2(null);
      setSt3(null);
      setSt4(null);
    };
    $[16] = st1;
    $[17] = st2;
    $[18] = st3;
    $[19] = st4;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  let t6;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [];
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  useEffect(t5, t6);
  let t7;
  if ($[22] !== classNames) {
    t7 = cn(classNames, "horizontal");
    $[22] = classNames;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== isMobile || $[25] !== viewport) {
    t8 = !MAIN_SLIDER_DATA_29719 && scrollInfo && <div className={style.horizontalScrollWrapper} ref={horizontalRef}>{scrollInfo.map(item => <div key={item.src} className={cn(style.horizontalScrollItem, item.style)} style={{
        transform: `rotate(${item.rotate}deg)`,
        top: `${item.top}%`
      }}><SafeHtmlRenderer tag="div" className={style.horizontalScrollImgTitle} content={item.text || ""} /><ParallaxMedia onMove={true} src={item.src} src375={item.srcXS} className={style.horizontalScrollImg} />{viewport && <NextImage loading="lazy" decoding="async" src={!isMobile ? Gradient_5 : Gradient_4_mob} alt="gradient" className={style.horizontalScrollImgGradient} />}</div>)}</div>;
    $[24] = isMobile;
    $[25] = viewport;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== isMobile || $[28] !== items || $[29] !== viewport) {
    t9 = MAIN_SLIDER_DATA_29719 && items.length > 0 && <div className={style.horizontalScrollWrapper} ref={horizontalRef}>{items.map((item_0, index_2) => <div key={item_0.id} className={cn(style.horizontalScrollItem, item_0.style)} style={{
        transform: `rotate(${slidesPositions[index_2].rotate}deg)`,
        top: `${slidesPositions[index_2].top}%`
      }}><div className={style.horizontalScrollImgTitle} dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(item_0.title || "")
        }} /><ParallaxMedia onMove={true} src={`${LINK_PREFIX}${item_0.background_desktop}`} src375={`${LINK_PREFIX}${item_0.background_mobile}`} className={style.horizontalScrollImg} />{viewport && <NextImage loading="lazy" decoding="async" src={!isMobile ? Gradient_5 : Gradient_4_mob} alt="gradient" className={style.horizontalScrollImgGradient} />}</div>)}</div>;
    $[27] = isMobile;
    $[28] = items;
    $[29] = viewport;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== t7 || $[32] !== t8 || $[33] !== t9) {
    t10 = <div className={t7} ref={scrollRef}>{t8}{t9}</div>;
    $[31] = t7;
    $[32] = t8;
    $[33] = t9;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  return t10;
}
function _temp(prev) {
  return cn(prev, style.__horizontalScroll);
}
export default HorizontalScroll;